import { useGMapCover } from '/@/hooks/use-g-map-cover'
import type { RootStateType } from '/@/store'
import type { GeojsonCoordinate } from '/@/types/map'
import type { WaylinePreviewModel } from '/@/utils/parse-wayline-kmz'
import { resolveWaylineRoutePaths } from '/@/utils/mediaWaylineOverlay'
import { wgs84togcj02 } from '/@/vendors/coordtransform'

const FIT_PADDING = [88, 88, 88, 88] as [number, number, number, number]
const FIT_MAX_ZOOM = 24
const FIT_MIN_ZOOM = 5
const SINGLE_POINT_ZOOM = 16
const MIN_SPAN_DEG = 0.0002

function toGcj (c: GeojsonCoordinate): GeojsonCoordinate {
  return wgs84togcj02(c[0], c[1]) as GeojsonCoordinate
}

function pickTitleAnchor (m: WaylinePreviewModel, g: typeof toGcj): GeojsonCoordinate | null {
  if (m.path.length) return g(m.path[0])
  if (m.waypoints.length) return g(m.waypoints[0].coord)
  if (m.polygons[0]?.ring.length) return g(m.polygons[0].ring[0])
  if (m.takeoff) return g(m.takeoff.coord)
  if (m.rth) return g(m.rth.coord)
  return null
}

function collectAllFitPoints (m: WaylinePreviewModel, g: typeof toGcj): GeojsonCoordinate[] {
  const pts: GeojsonCoordinate[] = []
  m.polygons.forEach(p => p.ring.forEach(c => pts.push(g(c))))
  m.path.forEach(c => pts.push(g(c)))
  m.waypoints.forEach(w => pts.push(g(w.coord)))
  if (m.takeoff) pts.push(g(m.takeoff.coord))
  if (m.rth) pts.push(g(m.rth.coord))
  return pts
}

function collectOverlaysForFit (hook: ReturnType<typeof useGMapCover>, ids: string[]) {
  const out: any[] = []
  ids.forEach(id => {
    const els = hook.getElementFromMap(id)
    if (els?.length) out.push(...els)
  })
  return out
}

export function fitMapToWaylinePath (map: any, AMap: any, path: GeojsonCoordinate[], previewOverlays?: any[]) {
  if (!path.length || !AMap?.LngLat) {
    return
  }

  if (path.length === 1) {
    const [lng, lat] = path[0]
    if (typeof map.setZoomAndCenter === 'function') {
      map.setZoomAndCenter(SINGLE_POINT_ZOOM, new AMap.LngLat(lng, lat))
    } else {
      map.setCenter?.(new AMap.LngLat(lng, lat))
      map.setZoom?.(SINGLE_POINT_ZOOM)
    }
    return
  }

  const lngs = path.map(p => p[0])
  const lats = path.map(p => p[1])
  let swLng = Math.min(...lngs)
  let swLat = Math.min(...lats)
  let neLng = Math.max(...lngs)
  let neLat = Math.max(...lats)
  if (neLng - swLng < MIN_SPAN_DEG) {
    const half = MIN_SPAN_DEG / 2
    swLng -= half
    neLng += half
  }
  if (neLat - swLat < MIN_SPAN_DEG) {
    const half = MIN_SPAN_DEG / 2
    swLat -= half
    neLat += half
  }

  const sw = new AMap.LngLat(swLng, swLat)
  const ne = new AMap.LngLat(neLng, neLat)

  if (typeof map.setBounds === 'function' && AMap.Bounds) {
    const bounds = new AMap.Bounds(sw, ne)
    map.setBounds(bounds, false, FIT_PADDING)
  } else if (typeof map.setFitView === 'function' && previewOverlays?.length) {
    try {
      map.setFitView(previewOverlays, false, [...FIT_PADDING], FIT_MAX_ZOOM)
    } catch {
      map.setFitView(previewOverlays, false, FIT_PADDING)
    }
  }

  const clampZoom = () => {
    const z = map.getZoom?.()
    if (typeof z !== 'number' || Number.isNaN(z)) return
    if (z > FIT_MAX_ZOOM) {
      map.setZoom?.(FIT_MAX_ZOOM)
    } else if (z < FIT_MIN_ZOOM) {
      map.setZoom?.(FIT_MIN_ZOOM)
    }
  }
  setTimeout(clampZoom, 80)
  setTimeout(clampZoom, 280)
}

export function hasWaylinePreviewGeometry (m: WaylinePreviewModel): boolean {
  return (
    resolveWaylineRoutePaths(m).some(p => p.length >= 2) ||
    m.polygons.length > 0 ||
    !!m.takeoff ||
    !!m.rth
  )
}

export function clearWaylinePreviewOnGMap (store: { state: RootStateType }, prefix: string) {
  const hook = useGMapCover()
  const ids = Object.keys(store.state.coverMap).filter(k => k.startsWith(prefix))
  ids.forEach(id => {
    if (hook.getElementFromMap(id)?.length) {
      hook.removeCoverFromMap(id)
    }
  })
}

/** 在共享 GMap 上绘制 KMZ 解析结果（折线、任务区、起飞/返航；不绘制航点/拍摄点标记）。 */
export function renderWaylineModelOnGMap (
  root: { $map?: unknown, $aMap?: unknown },
  model: WaylinePreviewModel,
  displayName: string,
  prefix: string
) {
  const map = root.$map as any
  const AMap = root.$aMap as any
  if (!map || !AMap || !hasWaylinePreviewGeometry(model)) {
    return
  }

  const hook = useGMapCover()
  const shortTitle = displayName.length > 40 ? `${displayName.slice(0, 38)}…` : displayName
  const createdIds: string[] = []

  model.polygons.forEach((poly, i) => {
    const ring = poly.ring.map(toGcj)
    if (ring.length < 3) return
    const id = `${prefix}poly_${i}`
    createdIds.push(id)
    hook.initPolygon(poly.name || `任务区 ${i + 1}`, [ring], '#1677ff', { id, name: poly.name })
  })

  const routePaths = resolveWaylineRoutePaths(model)
  routePaths.forEach((path, i) => {
    if (path.length < 2) return
    const gcjPath = path.map(toGcj)
    const id = routePaths.length === 1 ? `${prefix}line` : `${prefix}line_${i}`
    const lineName = routePaths.length === 1 ? displayName : `${displayName} ${i + 1}`
    createdIds.push(id)
    hook.initPolyline(lineName, gcjPath, '#1677ff', { id, name: lineName })
  })

  if (model.takeoff) {
    const c = toGcj(model.takeoff.coord)
    const id = `${prefix}takeoff`
    createdIds.push(id)
    hook.init2DPinGrouped(model.takeoff.name || '起飞', c, '19be6b', { id, name: '起飞' }, { content: '起' })
  }

  if (model.rth) {
    const c = toGcj(model.rth.coord)
    const id = `${prefix}rth`
    createdIds.push(id)
    hook.init2DPinGrouped(model.rth.name || '返航', c, 'e23c39', { id, name: '返航' }, { content: '返' })
  }

  const titlePos = pickTitleAnchor(model, toGcj)
  if (titlePos) {
    const tid = `${prefix}title`
    createdIds.push(tid)
    hook.initTextInfo(shortTitle, titlePos, tid, {
      background: 'rgba(255,255,255,0.92)',
      border: '1px solid rgba(22,119,255,0.35)',
      borderStyle: 'solid',
      borderRadius: '6px',
      padding: '2px 8px',
      fontSize: '13px',
      color: '#1677ff',
      fontWeight: '600'
    })
  }

  const allPts = collectAllFitPoints(model, toGcj)
  if (allPts.length) {
    const overlays = collectOverlaysForFit(hook, createdIds)
    fitMapToWaylinePath(map, AMap, allPts, overlays)
  }
}
